import React, { Component } from 'react';

interface InfiniteScrollProps {
  hasMore: boolean;
  loadMore: Function;
  useWindow: boolean;
  pageStart: number;
  threshold: number;
  loader?: JSX.Element;
  useCapture?: boolean;
  initialLoad?: boolean;
}

class InfiniteScroll extends Component<InfiniteScrollProps, {}> {
  pageLoaded = 0;
  scrollComponent = React.createRef<HTMLDivElement>();

  detachMousewheelListener() {
    const { useWindow, useCapture } = this.props;
    let scrollEl: any = window;

    if (useWindow === false && this.scrollComponent.current) {
      scrollEl = this.scrollComponent.current.parentNode;
    }
    scrollEl.removeEventListener(
      'mousewheel',
      this.mousewheelListener,
      useCapture
    );
  }

  detachScrollListener() {
    const { useWindow, useCapture } = this.props;

    let scrollEl: any = window;
    if (useWindow === false && this.scrollComponent.current) {
      scrollEl = this.scrollComponent.current.parentNode;
    }

    scrollEl.removeEventListener('scroll', this.scrollListener, useCapture);
    scrollEl.removeEventListener('resize', this.scrollListener, useCapture);
  }

  attachScrollListener() {
    const { useWindow, hasMore, useCapture, initialLoad } = this.props;

    if (!hasMore) {
      return;
    }

    let scrollEl: any = window;
    if (useWindow === false && this.scrollComponent.current) {
      scrollEl = this.scrollComponent.current.parentNode;
    }

    scrollEl.addEventListener(
      'mousewheel',
      this.mousewheelListener,
      useCapture
    );
    scrollEl.addEventListener('scroll', this.scrollListener, useCapture);
    scrollEl.addEventListener('resize', this.scrollListener, useCapture);

    if (initialLoad) {
      this.scrollListener();
    }
  }

  mousewheelListener(e: WheelEvent) {
    // Prevents Chrome hangups
    // See: https://stackoverflow.com/questions/47524205/random-high-content-download-time-in-chrome/47684257#47684257
    if (e.deltaY === 1) {
      e.preventDefault();
    }
  }

  scrollListener = () => {
    const el: any = this.scrollComponent.current;

    const scrollEl = window;
    const { useWindow, threshold, loadMore } = this.props;

    let offset;
    if (useWindow) {
      const doc =
        document.documentElement || document.body;
      const scrollTop =
        scrollEl.pageYOffset !== undefined
          ? scrollEl.pageYOffset
          : doc.scrollTop;

      offset =
        this.calculateTopPosition(el) +
        (el.offsetHeight - scrollTop - window.innerHeight);
    } else if (el.parentNode) {
      offset =
        el.scrollHeight - el.parentNode.scrollTop - el.parentNode.clientHeight;
    }

    if (offset < Number(threshold)) {
      this.detachScrollListener();
      // Call loadMore after detachScrollListener to allow for non-async loadMore functions
      if (typeof loadMore === 'function') {
        loadMore((this.pageLoaded += 1));
      }
    }
  };

  calculateTopPosition(el: any): any {
    if (!el) {
      return 0;
    }
    return el.offsetTop + this.calculateTopPosition(el.offsetParent);
  }

  componentDidMount() {
    this.pageLoaded = this.props.pageStart;
    this.attachScrollListener();
  }

  componentDidUpdate() {
    this.attachScrollListener();
  }

  componentWillUnmount() {
    this.detachScrollListener();
    this.detachMousewheelListener();
  }

  render() {
    const {
      children,
      hasMore,
      initialLoad,
      loader,
      loadMore,
      pageStart,
      threshold,
      useCapture,
      useWindow,
      ...props
    } = this.props;

    const childrenArray = [children];

    if (hasMore) {
      if (loader) {
        childrenArray.push(loader);
      }
    }

    return (
      <div ref={this.scrollComponent} {...props}>
        {childrenArray}
      </div>
    );
  }
}

export default InfiniteScroll;
